import { CashierConfig, DEFAULT_CASHIER_CONFIG_ITEM } from '@/models/cashier-settings/cashier-settings.model';
import { defineStore } from 'pinia';
import { api } from '@/services/http.service';
import { ElNotification } from 'element-plus';

type CashierSettingsStore = {
  isCashierConfigListLoading: boolean;
  isCurrencyFormChanged: boolean;
  isCreateCashierConfigLoading: boolean;
  cashierConfigList: CashierConfig[];
  selectedCountryConfig: {
    country: string;
    config: CashierConfig | null;
  };
  sort: string;
}

export const useCashierSettingsStore = defineStore({
  id: 'CashierSettingsStore',
  state: (): CashierSettingsStore => ({
    isCashierConfigListLoading: false,
    isCreateCashierConfigLoading: false,
    cashierConfigList: [],
    isCurrencyFormChanged: false,
    selectedCountryConfig: {
      country: '',
      config: null,
    },
    sort: '',
  }),
  getters: {
    getCurrencyListFromConfig(): (config: CashierConfig | null) => string[] {
      return (config) => {
        return config
          ? Array.from(
            new Set(
              [
                ...(config.defaultAmount?.depositList ?? []).map(item => item.currency),
                ...(config.defaultAmount?.withdrawalList ?? []).map(item => item.currency),
                ...(config.suggestedAmount?.depositList ?? []).map(item => item.currency),
                ...(config.suggestedAmount?.withdrawalList ?? []).map(item => item.currency),
              ],
            ))
          : [];
      };
    },
  },
  actions: {
    setCashierConfigLoading(isLoading: boolean): void {
      this.isCashierConfigListLoading = isLoading;
    },
    setCreateCashierConfigLoading(isLoading: boolean): void {
      this.isCreateCashierConfigLoading = isLoading;
    },
    setCashierSort(sortOrder: string): void {
      this.sort = sortOrder;
    },
    resetCashierSort(): void {
      this.sort = '';
    },
    resetSelectedCountryConfig(): void {
      this.selectedCountryConfig = {
        country: '',
        config: DEFAULT_CASHIER_CONFIG_ITEM,
      };
    },
    setSelectedCountry(country: string): void {
      this.selectedCountryConfig.country = country;
    },
    setCurrencyFormChanged(isChanged: boolean): void {
      this.isCurrencyFormChanged = isChanged;
    },
    getCashierConfigList(): Promise<void> {
      this.setCashierConfigLoading(true);

      return api.cashierSettings.getCashierConfigListRequest.perform({
        sort: this.sort,
      })
        .then(data => {
          this.cashierConfigList = data;
        })
        .finally(() => {
          this.setCashierConfigLoading(false);
        });
    },
    async getCashierConfig(country: string): Promise<void> {
      this.setCashierConfigLoading(true);

      return await api.cashierSettings.getCashierConfigRequest.perform(country)
        .then(data => {
          this.selectedCountryConfig.config = data;
        })
        .finally(() => {
          this.setCashierConfigLoading(false);
        });
    },
    async createCashierConfig(country: string, countryName: string) {
      this.setCreateCashierConfigLoading(true);

      return await api.cashierSettings.createCashierConfig.perform(country)
        .then(() => {
          this.setSelectedCountry(country);
          ElNotification({
            title: 'Success',
            message: `${countryName} cashier configuration successfully created.`,
            type: 'success',
          });
        })
        .finally(() => {
          this.setCreateCashierConfigLoading(false);
        });
    },
    async updateCashierConfig(config: CashierConfig, countryName: string) {
      return await api.cashierSettings.updateCashierConfig.perform(config.country, config)
        .then(() => {
          ElNotification({
            title: 'Success',
            message: `${countryName} cashier configuration successfully saved.`,
            type: 'success',
          });
        });
    },
  },
});
